// src/pages/SelectElements.jsx
import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate }         from 'react-router-dom';
import axios                              from 'axios';
import Table                              from '../shared/table/Table';
import { FiTrash2, FiPlus, FiSave }       from 'react-icons/fi';
import { toast, ToastContainer }          from 'react-toastify';
import { confirmAlert }                   from 'react-confirm-alert';
import 'react-toastify/dist/ReactToastify.css';
import 'react-confirm-alert/src/react-confirm-alert.css';
import useTranslations from "../../hooks/useTranslations";
import useUserLanguage  from "../../hooks/useUserLanguage";

export default function SelectElements() {
  const { slug, name } = useParams();
  const navigate       = useNavigate();
  const baseURL        = `${process.env.REACT_APP_API_BASE_URL}/api/modules/${slug}/elements/${name}/options`;

  const userLanguage                    = useUserLanguage();
  const { translations: trans }         = useTranslations(userLanguage);
  const getText = (key, fallback = "") => trans[key] || fallback;

  const [options, setOptions] = useState([]);
  const [rows,    setRows]    = useState([{ value: '', title: '', status: 'active' }]);

  const getOptions = async () => {
    try {
      const { data } = await axios.get(baseURL);
      setOptions(data);
    } catch {
      toast.error(getText('SELECT_ELEMENTS_FETCH_ERROR'));
    }
  };
  useEffect(() => { getOptions(); }, [slug, name]);

  const addRow = () => setRows(r => [...r, { value:'', title:'', status:'active' }]);
  const onInput = (i, field, val) =>
    setRows(r => r.map((row, idx) => idx === i ? { ...row, [field]: val } : row));

  const saveRows = async () => {
    const valid = rows.filter(r => r.value.trim() && r.title.trim());
    if (!valid.length) {
      toast.warn(getText('SELECT_ELEMENTS_NO_VALID_ROW'));
      return;
    }
    try {
      for (const r of valid) {
        await axios.post(baseURL, { value: r.value, title: r.title, status: r.status });
      }
      toast.success(getText('SELECT_ELEMENTS_SAVED'));
      setRows([{ value: '', title: '', status: 'active' }]);
      getOptions();
    } catch (err) {
      console.error(err);
      toast.error(getText('SELECT_ELEMENTS_SAVE_ERROR'));
    }
  };

  const del = (index) => confirmAlert({
    title:   getText('SELECT_ELEMENTS_DELETE_TITLE'),
    message: getText('SELECT_ELEMENTS_DELETE_MESSAGE'),
    buttons: [
      {
        label: getText('SELECT_ELEMENTS_DELETE_CONFIRM'),
        onClick: async () => {
          try {
            await axios.delete(`${baseURL}/${index}`);
            getOptions();
          } catch {
            toast.error(getText('SELECT_ELEMENTS_DELETE_ERROR'));
          }
        }
      },
      { label: getText('SELECT_ELEMENTS_CANCEL') }
    ]
  });

  const StatusSwitch = React.memo(({ order, current }) => {
    const [checked, setChecked] = useState(current === 'active');
    const toggle = async () => {
      try {
        await axios.put(`${baseURL}/${order}/toggle`);
        setChecked(c => !c);
        getOptions();
      } catch {
        toast.error(getText('SELECT_ELEMENTS_STATUS_ERROR'));
      }
    };
    return (
      <div className="form-check form-switch text-end">
        <input
          className="form-check-input"
          type="checkbox"
          checked={checked}
          onChange={toggle}
          style={{ width:45, height:22 }}
        />
      </div>
    );
  });

  const columns = useMemo(() => [
    {
      id: 'rowNo',
      header: getText('SELECT_ELEMENTS_COL_ID'),
      cell: info => info.row.index + 1,
      size: 60,
      meta: { headerClassName: 'text-start', className: 'text-start' }
    },
    {
      accessorKey: 'value',
      header: getText('SELECT_ELEMENTS_COL_VALUE'),
      meta: { headerClassName: 'text-start', className: 'text-start' }
    },
    {
      accessorKey: 'title',
      header: getText('SELECT_ELEMENTS_COL_TITLE'),
      meta: { headerClassName: 'text-start', className: 'text-start' }
    },
    {
      accessorKey: 'status',
      header: () => <div className="text-end">{getText('SELECT_ELEMENTS_COL_STATUS')}</div>,
      cell: info => (
        <StatusSwitch
          order={info.row.original.order}
          current={info.getValue()}
        />
      ),
      meta: { className: 'text-end' }
    },
    {
      id: 'actions',
      header: () => <div className="text-end">{getText('SELECT_ELEMENTS_COL_ACTIONS')}</div>,
      cell: info => (
        <button
          className="avatar-text avatar-md text-danger"
          onClick={() => del(info.row.index)}
          title={getText('DELETE')}
        >
          <FiTrash2 />
        </button>
      ),
      meta: { className: 'text-end' },
      enableSorting: false
    }
  ], [options, trans]);

  return (
    <div className="container mt-4">
      <ToastContainer />
      <div className="d-flex mb-3">
        <button className="btn btn-outline-secondary" onClick={() => navigate(-1)}>
          {getText('SELECT_ELEMENTS_BACK')}
        </button>
      </div>

      <Table data={options} columns={columns} />

      <div className="card mt-4 p-3">
        <h5>{getText('SELECT_ELEMENTS_ADD_TITLE')}</h5>
        {rows.map((r, i) => (
          <div key={i} className="row g-3 mb-2">
            <div className="col-md-5">
              <input
                className="form-control"
                placeholder={getText('SELECT_ELEMENTS_PLACEHOLDER_VALUE')}
                value={r.value}
                onChange={e => onInput(i, 'value', e.target.value)}
              />
            </div>
            <div className="col-md-5">
              <input
                className="form-control"
                placeholder={getText('SELECT_ELEMENTS_PLACEHOLDER_TITLE')}
                value={r.title}
                onChange={e => onInput(i, 'title', e.target.value)}
              />
            </div>
          </div>
        ))}

        <div className="d-flex justify-content-between">
          <button className="btn btn-outline-primary" onClick={addRow}>
            <FiPlus /> {getText('SELECT_ELEMENTS_ADD_ROW')}
          </button>
          <button className="btn btn-primary" onClick={saveRows}>
            <FiSave /> {getText('SELECT_ELEMENTS_SAVE')}
          </button>
        </div>
      </div>
    </div>
  );
}